import type {
  GridColumnExtension,
  Sorting,
  SortingState
} from '@devexpress/dx-react-grid';
import type { BrowserHistory, Location } from 'history';
import { t } from 'i18next';
import { BASE_SERVER_URL, IS_ENCRYPTION_ENABLED } from 'src/config';
import type { ChatFile } from 'src/content/Chat/types';
import { getEncryptParams } from 'src/keycloak';
import type { UserFavourites } from 'src/redux/slices/data';
import { initialColorList } from 'src/theme/customColors';
import type { DataApiInputParams, IResourceProvider } from 'src/types/api';
import type { SelectItem, StringKeys } from 'src/types/base';
import type { Document, FolderStructure, IFile } from 'src/types/document';
import {
  DEFAULT_CONTENT_ROLES,
  DOCUMENT_STATUS,
  RESOURCE_PROVIDERS,
  RESOURCE_TYPE,
  UPLOAD_TYPE,
  USER_APP_PERMISSIONS,
  USER_RESOURCE_PERMISSION
} from 'src/types/enum';
import { DATE_FORMAT, MAX_NAME_LENGTH } from 'src/utils/constants';
import { getParam, getPayload } from 'src/utils/encryption';
import { compressEncryptPool } from 'src/utils/workers/compressEncryptPool';
import { TRANSLATION_CONSTANTS as T } from 'src/utils/translations';
import { formatDate } from 'src/utils/utils';
import { v4 as uuidV4 } from 'uuid';

export const defaultFilters = {
  tag_id: []
};

export enum EXEMPTED_WORKSPACES_COLLECTIONS {
  FOUND = RESOURCE_TYPE.FOUND_WORKSPACE,
  PUBLIC = RESOURCE_TYPE.PUBLIC_WORKSPACE,
  PRIVATE = RESOURCE_TYPE.PRIVATE_WORKSPACE,
  FOUND_COLLECTION = RESOURCE_TYPE.FOUND_COLLECTION
}

export const UPLOAD_CONSTRAINT_RESOURCES = [
  RESOURCE_TYPE.WORKSPACE,
  RESOURCE_TYPE.PUBLIC_WORKSPACE,
  RESOURCE_TYPE.PRIVATE_WORKSPACE,
  RESOURCE_TYPE.FOUND_WORKSPACE
];

export const MOVEABLE_RESOURCES = [RESOURCE_TYPE.FOLDER, RESOURCE_TYPE.FILE];
export const PAGE_SIZES = [25, 50, 100];
export const ROOT_WORKSPACE_PAGE_SIZE = 100000;
export const EDIT_COLUMN_WIDTH = 150;

export const initialNewRow = {
  id: 0,
  display_name: ''
};
export const ORGANIZATION_ADMINISTRATOR = 'Organization Administrators';
export const ORGANIZATION_OWNERS = 'Organization Owners';
export const ORGANIZATION_OWNER = 'Organization Owner';

export const ADVANCED_SHARING_OPTIONS = [
  {
    name: T.keepManuallyAssignedRolesOnSubFoldersFiles,
    value: 'default'
  },
  {
    name: T.assignRoleToAllSubFoldersFiles,
    value: 'force'
  },
  { name: T.assignRoleOnFilesOnly, value: 'files-only' }
];

export const no_data_message = (
  resourceType,
  resourcePermissions,
  appPermissions,
  filtersApplied = false
) => {
  if (filtersApplied) return t(T.noData);
  switch (resourceType) {
    case RESOURCE_TYPE.NAMESPACE:
      return appPermissions?.[USER_APP_PERMISSIONS.CREATE_ROOT_FOLDER_CONTENT]
        ?.value
        ? t(T.noDataNameSpaceMessage)
        : t(T.noDataNoPermissionMessage);
    case RESOURCE_TYPE.WORKSPACE:
      return resourcePermissions?.[USER_RESOURCE_PERMISSION.CREATE_FOLDERS]
        ?.value ||
        resourcePermissions?.[USER_RESOURCE_PERMISSION.UPLOAD_FILES_FOLDERS]
          ?.value
        ? t(T.noDataWorkspaceMessage)
        : t(T.noDataNoPermissionMessage);
    case RESOURCE_TYPE.COLLECTION:
    case RESOURCE_TYPE.FOLDER:
      return resourcePermissions?.[USER_RESOURCE_PERMISSION.CREATE_FOLDERS]
        ?.value ||
        resourcePermissions?.[USER_RESOURCE_PERMISSION.UPLOAD_FILES_FOLDERS]
          ?.value
        ? t(T.noDataCollectionMessage)
        : t(T.noDataNoPermissionMessage);
    default:
      return t(T.noData);
  }
};

export const COLUMNS = {
  FILENAME: 'name',
  MODIFIED_BY: 'modified_by',
  MODIFIED_ON: 'modified_on',
  SYNCED_BY: 'synced_by',
  SYNCED_ON: 'synced_on'
};

export const COLUMN_TITLES = {
  [COLUMNS.FILENAME]: T.name,
  [COLUMNS.MODIFIED_ON]: T.modifiedOn,
  [COLUMNS.MODIFIED_BY]: T.modifiedBy,
  [COLUMNS.SYNCED_BY]: T.syncedBy,
  [COLUMNS.SYNCED_ON]: T.syncedOn
};

export const ORDER_LIST = [
  { id: 1, name: t(T.name) },
  { id: 2, name: t(T.lastUpdated) }
];

export const documentsColumns = (isExternalResource: boolean) => [
  {
    name: COLUMNS.FILENAME,
    title: t(COLUMN_TITLES[COLUMNS.FILENAME]),
    getCellValue: (row) => ({
      name: row?.display_name,
      typeCode: row?.type_code
    })
  },
  {
    name: isExternalResource ? COLUMNS.SYNCED_BY : COLUMNS.MODIFIED_BY,
    title: isExternalResource
      ? t(COLUMN_TITLES[COLUMNS.SYNCED_BY])
      : `${t(COLUMN_TITLES[COLUMNS.MODIFIED_BY])} / ${t(
          COLUMN_TITLES[COLUMNS.SYNCED_BY]
        )}`,
    getCellValue: (row) =>
      row?.provider_id ? row?.synchronized_by : row?.modified_by || ''
  },
  {
    name: !!isExternalResource ? COLUMNS.SYNCED_ON : COLUMNS.MODIFIED_ON,
    title: !!isExternalResource
      ? t(COLUMN_TITLES[COLUMNS.SYNCED_ON])
      : `${t(COLUMN_TITLES[COLUMNS.MODIFIED_ON])} / ${t(
          COLUMN_TITLES[COLUMNS.SYNCED_ON]
        )}`,
    getCellValue: (row) =>
      row?.provider_id ? row?.synchronized_on : row?.modified_on || ''
  }
];

export const editingColumnExtensions = [
  {
    columnName: COLUMNS.FILENAME,
    createRowChange: (row, value) => ({
      ...row,
      display_name: value
    })
  }
];

export const documentsColumnsExtensions = (
  isExternalResource: boolean,
  isMobile: boolean
) => [
  {
    columnName: COLUMNS.FILENAME,
    width: isMobile ? '35%' : '45%'
  },

  {
    columnName: !!isExternalResource ? COLUMNS.MODIFIED_BY : COLUMNS.SYNCED_BY,
    width: '16%'
  },
  {
    columnName: !!isExternalResource ? COLUMNS.MODIFIED_ON : COLUMNS.SYNCED_ON,
    width: '15%'
  }
];

export const getColumnExtensionsWidth = (
  columnExtensions: GridColumnExtension[],
  totalWidth?: number,
  modalOpen?: boolean
) => {
  if (totalWidth > 0) {
    const originalWidth = totalWidth > 768 ? totalWidth : 768;
    return columnExtensions.map((column) => {
      const width =
        (parseInt(column?.width?.toString().split('%')[0] || '') / 100) *
        (originalWidth - (modalOpen ? 0 : EDIT_COLUMN_WIDTH));

      return {
        ...column,
        width: width
      };
    });
  }
  return [];
};

export type TagType = (SelectItem | string)[];
export type GroupedFilesType = { [z: string]: any }[][];
export type FilesUploadStatus = {
  failed: number[];
  success: number[];
  details?: any;
};
export type PlaceholderTextType = {
  createText: string;
  uploadText: string;
  description: string;
};

export const getTagApiParams = (
  data: (string | number)[],
  tagsList: TagType
): DataApiInputParams => {
  let tags = tagsList.map((item) =>
    typeof item === 'string' ? item : item?.name
  );

  return {
    params: {
      file_id: data.toString(),
      tag: tags.toString()
    }
  };
};

export const getDocumentDeleteApiParams = (
  documentId: (number | string) | (number | string)[]
) => {
  return {
    params: {
      id: Array.isArray(documentId) ? documentId.join() : documentId
    }
  };
};

export const getReprocessDocuments = (
  selectedDocs: (string | number)[],
  rows: any[]
) => {
  const allowedFiles = rows.filter(
    (item) =>
      selectedDocs.includes(item.id) &&
      item.status_code === DOCUMENT_STATUS.UNSUPPORTED
  );
  return {
    documentCount: allowedFiles.length,
    documentIds: allowedFiles.map((item) => item.id).join()
  };
};

export type UploadedFileRef = { name: string; id: number };

/**
 * Extracts `{name, id}` refs for newly uploaded files from a `files/file`
 * (`upload_type=chat_file`) response: `data.files[workspaceId].children[folderId].children[fileKey] = {name, id}`.
 * Accepts either the unwrapped response (`{data:{files:...}}`) or a bare `{files:...}` object.
 */
export const extractUploadedFileRefs = (response: any): UploadedFileRef[] => {
  const filesNode = response?.data?.files ?? response?.files;
  if (!filesNode || typeof filesNode !== 'object') return [];
  const workspaceId = Object.keys(filesNode)[0];
  const workspace = filesNode[workspaceId];
  const folderId = Object.keys(workspace?.children || {})[0];
  const folder = workspace?.children?.[folderId];
  return Object.values(folder?.children || {}) as UploadedFileRef[];
};

export const getFormData = async (
  filesList,
  tagsList,
  transactionId: string,
  uploadType?: UPLOAD_TYPE,
  parentId?: string | number,
  isChatAttachments?: boolean,
  isSmartMeetUpload?: boolean,
  isFileProcessingRequired?: boolean,
  extraArgs: Record<string, any> = {}
): Promise<FormData> => {
  const formData = new FormData();
  if (parentId) {
    formData.append('parent_id', getParam(parentId));
  }
  if (isChatAttachments || isSmartMeetUpload) {
    formData.append('upload_type', getParam(UPLOAD_TYPE.CHAT_FILE));
  } else if (uploadType) {
    formData.append('upload_type', getParam(uploadType));
  } else if (filesList?.some((file) => ((file as any).webkitRelativePath || '').split('/').length > 1)) {
    formData.append('upload_type', getParam(UPLOAD_TYPE.FOLDER));
  } else {
    formData.append('upload_type', getParam(UPLOAD_TYPE.FILE));
  }

  if (!isFileProcessingRequired) {
    formData.append('is_processing_required', getParam(false));
  }

  if (transactionId)
    formData.append('transaction_id', getParam(transactionId));
  if (tagsList?.length) {
    let tags = tagsList.map((item) =>
      typeof item === 'string' ? item : item?.name
    );
    formData.append('tag', getParam(tags.toString()));
  }

  if (extraArgs) {
    Object.entries(extraArgs).forEach(([key, value]) => {
      formData.append(key, getParam(value));
    });
  }

  if (IS_ENCRYPTION_ENABLED) {
    formData.append('payload', getPayload('', getEncryptParams()));
  }
  const key = IS_ENCRYPTION_ENABLED ? getEncryptParams()?.k : null;
  await Promise.all(filesList.map(async (file) => {
    const uuid = `${uuidV4()}.pdf`;
    const uuidName = `${uuid}_name`;
    const uuidPath = `${uuid}_path`;
    const uuidIsCompressed = `${uuid}_is_compressed`;
    const uuidFileSize = `${uuid}_file_size`;

    // Use webkitRelativePath as primary source (folder uploads) with safe fallback.
    let filePath: string = (file as any).webkitRelativePath || file.path || '';
    if (filePath[0] === '/') filePath = filePath.replace(/^\/+/, '');
    filePath = filePath.split('/').slice(0, -1).join('/');

    formData.append(uuidName, getParam(file.name));
    formData.append(uuidPath, getParam(filePath));
    formData.append(uuidFileSize, getParam(file.size));

    const result = await compressEncryptPool.process(file, key, IS_ENCRYPTION_ENABLED);
    const finalFile = result.buffer
      ? new File([result.buffer], uuid, { type: file.type, lastModified: file.lastModified })
      : new File([file], uuid, { type: file.type, lastModified: file.lastModified });

    formData.append(uuidIsCompressed, getParam(result.wasCompressed));
    formData.append('file', finalFile);
  }));
  return formData;
};

export const areFilesValid = (
  acceptedFiles: File[],
  fileNameLimit: number = 200
): { isValid: boolean; errorMsg?: string; errorFile?: File } => {
  for (const file of acceptedFiles) {
    if (file.name.length > fileNameLimit) {
      return {
        isValid: false,
        errorMsg: `File name exceeds limit: ${file.name.slice(0, 15)}...`,
        errorFile: file
      };
    }
  }
  return { isValid: true };
};

export type GetFormDataNewOptions = {
  /**
   * When true, multipart contains only `upload_type`, `transaction_id`, optional `payload`
   * (when client encryption is enabled), and `file` — for `core/v1/workflows-resources`.
   * Skips `parent_id`, `extraArgs`, and workspace `uuid_*_name` / `uuid_*_path` metadata.
   */
  workflowResourcesOnly?: boolean;
  /** Maps relative_path → fs_incoming_files.id for session-aware uploads. */
  incomingFileMap?: Map<string, number>;
};

export const getFormDataNew = async (
  filesList: File[],
  transactionId: string | undefined,
  uploadType?: UPLOAD_TYPE,
  parentId?: string | number,
  extraArgs: Record<string, any> = {},
): Promise<FormData> => {
  const formData = new FormData();

  if (parentId) {
    formData.append('parent_id', getParam(parentId));
  }

  let resolvedUploadType = uploadType;
  if (!resolvedUploadType) {
    resolvedUploadType = filesList?.some(
      (file: any) => ((file as any).webkitRelativePath || '').split('/').length > 1
    )
      ? UPLOAD_TYPE.FOLDER
      : UPLOAD_TYPE.FILE;
  }
  formData.append('upload_type', getParam(resolvedUploadType));

  if (transactionId) {
    formData.append('transaction_id', getParam(transactionId));
  }
  let incomingFileMap = extraArgs?.incomingFileMap;

  if (extraArgs && Object.keys(extraArgs).length > 0) {
    Object.entries(extraArgs).forEach(([key, value]) => {
      if (key === 'incomingFileMap') return;
      formData.append(key, getParam(value));
    });
  }

  if (IS_ENCRYPTION_ENABLED) {
    formData.append('payload', getPayload('', getEncryptParams()));
  }

  const key = IS_ENCRYPTION_ENABLED ? getEncryptParams()?.k : null;
  await Promise.all(filesList.map(async (file) => {
    const uuid = `${uuidV4()}.pdf`;
    const uuidName = `${uuid}_name`;
    const uuidPath = `${uuid}_path`;
    const uuidIsCompressed = `${uuid}_is_compressed`;
    const uuidFileSize = `${uuid}_file_size`;

    let rawPath = (file as any).webkitRelativePath || (file as any).path || '';
    if (rawPath?.[0] === '/') rawPath = rawPath.replace(/^\/+/, '');
    const directoryPath = rawPath?.split('/').slice(0, -1)?.join('/');

    formData.append(uuidName, getParam(file.name));
    formData.append(uuidPath, getParam(directoryPath));
    formData.append(uuidFileSize, getParam(file.size));

    if (incomingFileMap) {
      const relativePath = (file as any).webkitRelativePath || file.name;
      const incomingFileId = incomingFileMap.get(relativePath);
      if (incomingFileId != null) {
        formData.append(`${uuid}_incoming_file_id`, getParam(incomingFileId));
      }
    }

    const result = await compressEncryptPool.process(file, key, IS_ENCRYPTION_ENABLED);
    const finalFile = result.buffer
      ? new File([result.buffer], uuid, { type: file.type, lastModified: file.lastModified })
      : new File([file], uuid, { type: file.type, lastModified: file.lastModified });

    formData.append(uuidIsCompressed, getParam(result.wasCompressed));
    formData.append('file', finalFile);
  }));

  return formData;
};

const flattenHierarchy = (folderStructure: FolderStructure, result = []) => {
  Object.entries(folderStructure)?.map(([name, value]) => {
    if (typeof value === 'object' && !Array.isArray(value)) {
      result.push({
        name,

        type: UPLOAD_TYPE.FOLDER
      });
      flattenHierarchy(value, result);
    } else {
      result.push({ file: value[0], type: UPLOAD_TYPE.FILE });
    }
  });
  return result;
};

export const buildFlatHierarchy = (files: IFile[]) => {
  const hierarchy: Record<string, any> = {};
  files.forEach((file) => {
    const path = file?.path[0] === '/' ? file?.path.slice(1) : file?.path;
    const parts = path.split('/');
    let currentLevel = hierarchy;
    const lastIndex = parts.length - 1;
    parts.forEach((part, index) => {
      if (!currentLevel[part]) {
        currentLevel[part] = index === lastIndex ? [file] : {};
      } else if (index === lastIndex) {
        currentLevel[part].push(file);
      }
      currentLevel = currentLevel[part];
    });
  });
  return flattenHierarchy(hierarchy);
};

const validationRules = {
  display_name: {
    isValid: (value) => {
      return (
        !!value.trim() && value.length > 0 && value.length <= MAX_NAME_LENGTH
      );
    },
    errorText: t(T.nameCharacterLengthError)
  }
};

export const validate = (changed) => ({
  ...Object.keys(changed)
    .filter((key) => key === 'display_name')
    .reduce((acc, field) => {
      const isValid = validationRules[field].isValid(changed[field]);
      return {
        ...acc,
        [field]: {
          isValid,
          error: !isValid && validationRules[field].errorText
        }
      };
    }, {})
});

export const isRowValid = (changedObject) => {
  return Object.keys(initialNewRow).every((key) => {
    if (key === 'name') {
      return (
        changedObject?.hasOwnProperty(key) &&
        validationRules?.[key]?.isValid(changedObject[key] || '')
      );
    }
    return true;
  });
};

export const getFilteredList = (filtered: StringKeys) => {
  let filterObj = {};
  filtered &&
    Object.keys(filtered).forEach((key) => {
      if (key === 's') {
        filterObj[key] = filtered[key];
      } else if (key === 'uploaded_by') {
        filterObj[key] = filtered[key]?.username;
      } else if (
        key === 'status_id' &&
        filtered[key] &&
        filtered[key].value !== 'all'
      ) {
        filterObj[key] = filtered[key].id;
      } else if (key === 'tag_id') {
        if (filtered[key].length) {
          filterObj[key] = filtered[key].map((item) => item.id).toString();
        } else {
          delete filterObj[key];
        }
      }
    });
  return filterObj;
};

export const defaultSorting: Sorting[] = [
  {
    columnName: COLUMNS.FILENAME,
    direction: 'asc'
  }
];

export const sortingColumnExtensions: SortingState.ColumnExtension[] = [
  { columnName: COLUMNS.FILENAME, sortingEnabled: true },
  { columnName: COLUMNS.MODIFIED_BY, sortingEnabled: true },
  { columnName: COLUMNS.MODIFIED_ON, sortingEnabled: true }
];

export const sharingRoleDescriptions = {
  [DEFAULT_CONTENT_ROLES.VIEWER]: T.assignViewerSubtitle,
  [DEFAULT_CONTENT_ROLES.CONTROLLER]: T.assignControllerSubtitle,
  [DEFAULT_CONTENT_ROLES.CONTRIBUTOR]: T.assignContributorSubtitle
};

export const sharingAssistantRole = {
  [DEFAULT_CONTENT_ROLES.VIEWER]: T.assignAssistantViewer,
  [DEFAULT_CONTENT_ROLES.CONTROLLER]: T.assignAssignController
};

export const colorList: SelectItem[] = initialColorList.map((color, index) => ({
  id: index + 1,
  name: color
}));

export const checkPageReload = (
  history: BrowserHistory,
  location: Location
) => {
  const navigationEntries = performance.getEntriesByType('navigation');
  if (navigationEntries.length > 0) {
    const navigationEntry = navigationEntries[0];
    if ((navigationEntry as any).type === 'reload') {
      history.push(location.pathname, {
        state: null
      });
    }
  }
};

export const getFavouriteData = (rows, favourites, type) => {
  const favouriteArray = favourites?.filter((fav) => fav?.item_type === type);
  return rows?.map((row) => ({
    ...row,
    isFavourite: favouriteArray[0]?.items?.some(
      (fav: StringKeys) => fav?.item_id === row.id
    )
  }));
};

export const isLessThanOneMinuteOld = (createdOn: Date): boolean => {
  const currentTime = new Date();
  let createdOnDate = new Date(createdOn);
  const timeDifference = currentTime.getTime() - createdOnDate.getTime();

  return timeDifference < 240000;
};

export const publicOrPrivateWorkspaceCheck = (workSpaceCode: string) =>
  workSpaceCode === RESOURCE_TYPE.PUBLIC_WORKSPACE ||
  workSpaceCode === RESOURCE_TYPE.PRIVATE_WORKSPACE;

export const documentToChatFileMapper = (document: Document): ChatFile => {
  return {
    author: null,
    file_id: document.id,
    name: document.display_name || document.name,
    type_id: document.type_id,
    type_name: document.type_id_name || '',
    tag_name: 'null',
    tag_id: 0,
    format: document.format,
    original_format: document.original_format,
    created_on: formatDate(document.created_on, DATE_FORMAT),
    provider_id: document?.provider_id || null,
    parent_id: document?.parent_id || null,
    type_code: document.type_code,
    id: document.id?.toString()
  };
};

export const favouriteToChatFileMapper = (
  favourite: UserFavourites
): ChatFile => {
  return {
    author: null,
    file_id: favourite.item_id,
    name: favourite.display_name || favourite.name,
    type_id: favourite.type_id,
    type_name: '',
    tag_name: 'null',
    tag_id: 0,
    format: '',
    original_format: '',
    created_on: formatDate(favourite.created_on, DATE_FORMAT),
    provider_id: favourite?.provider_id || null,
    parent_id: favourite?.parent_id || null,
    type_code: favourite.code,
    id: favourite.item_id?.toString()
  };
};
export const SHARE_ASSISTANT_PATH = 'share-assistant';
export const SHARE_CHAT_PATH = 'share-chat';

export const shareResourceLink = (uuid: string, sharePath: string) =>
  `${BASE_SERVER_URL}/${sharePath}/${uuid}`;

export const resourceProviderNavigateLink = (
  itemData: StringKeys,
  resourceProvider: IResourceProvider
) => {
  switch (resourceProvider.type) {
    case RESOURCE_PROVIDERS.SHAREPOINT:
      return itemData?.webUrl;

    case RESOURCE_PROVIDERS.ONEDRIVE:
      return itemData?.webUrl;

    case RESOURCE_PROVIDERS.CONFLUENCE:
      return `${resourceProvider?.base_url}${itemData?._links?.webui}`;

    default:
      return '';
  }
};

export const hasMissingPermission = (row): boolean => {
  const required = [
    'delete-files-folders',
    'rename-files-folders',
    'upload-files-folders'
  ];

  return required.some((key) => !row?.permissions?.[key]?.value);
};

export const hasUnsupportedFiles = (rows): boolean => {
  return (
    Array.isArray(rows) && rows.some((r) => (r?.unsupported_files ?? 0) > 0)
  );
};
